import React from "react";
import { Dialog, DialogContent, Box } from "@mui/material";
import ImageUploadField from "../../../../components/ImageUploadField";
import Field from "../shared/Field";
import ItemCard from "../shared/ItemCard";
import AddItemBtn from "../shared/AddItemBtn";
import SectionTitle from "../shared/SectionTitle";
import ModalHeader from "../shared/ModalHeader";
import ModalActions from "../shared/ModalActions";
import { generateSlug } from "../utils/slugUtils";

const dialogSx = {
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    background: "#1a1a1a",
    color: "text.primary",
    maxHeight: "calc(100vh - 64px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    m: 2,
  },
};

const scrollbarSx = {
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.05)" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.2)",
    borderRadius: "4px",
  },
};

/**
 * CaseStudyModal
 *
 * Props (all come from useCaseStudy hook + setForm from usePageBuilder):
 *  open               {bool}
 *  caseStudyForm      {object}
 *  setCaseStudyForm   {fn}
 *  savingCaseStudy    {bool}
 *  onClose            {fn}
 *  onConfirm          {fn}   — () => handleAddCaseStudyToPage(setForm, setModalDraft)
 */
const CaseStudyModal = ({
  open,
  caseStudyForm,
  setCaseStudyForm,
  savingCaseStudy,
  onClose,
  onConfirm,
}) => {
  const update = (key) => (v) =>
    setCaseStudyForm((p) => ({ ...p, [key]: v }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={dialogSx}
    >
      <ModalHeader
        icon="💼"
        title="Add Project Details"
        subtitle="Fill in comprehensive details for this project"
        onClose={onClose}
      />

      <DialogContent
        sx={{ p: 3, pt: "20px", overflowY: "auto", flex: 1, ...scrollbarSx }}
      >
        {/* ── Basic Info ── */}
        <SectionTitle title="Basic Info" />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field
            label="Title"
            value={caseStudyForm.title}
            onChange={(v) => {
              const slug = generateSlug(v);
              setCaseStudyForm((p) => ({ ...p, title: v, slug }));
            }}
            placeholder="Comforty E-Commerce"
          />
          <Field
            label="Slug"
            value={caseStudyForm.slug}
            onChange={update("slug")}
            hint="Auto-generated"
          />
        </Box>
        <Field label="Headline" value={caseStudyForm.headline} onChange={update("headline")} placeholder="Modern Furniture Platform for Comforty.pk" />
        <Field label="Subheadline" value={caseStudyForm.subheadline} onChange={update("subheadline")} multiline placeholder="A premium online furniture platform..." />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field label="Category" value={caseStudyForm.category} onChange={update("category")} placeholder="E-Commerce Platform" />
          <Field label="Site URL" value={caseStudyForm.siteUrl} onChange={update("siteUrl")} placeholder="https://comforty.pk" />
        </Box>
        <ImageUploadField
          label="Thumbnail Image"
          value={caseStudyForm.thumbnailUrl}
          onChange={update("thumbnailUrl")}
        />

        {/* ── Client Info ── */}
        <SectionTitle title="Client Info" />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field label="Client" value={caseStudyForm.client} onChange={update("client")} placeholder="Comforty.pk" />
          <Field label="Industry" value={caseStudyForm.industry} onChange={update("industry")} placeholder="Furniture & Home Decor" />
        </Box>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field label="Timeline" value={caseStudyForm.timeline} onChange={update("timeline")} placeholder="4 months" />
          <Field label="Services" value={caseStudyForm.services} onChange={update("services")} placeholder="Full-Stack Development, UI/UX Design" />
        </Box>

        {/* ── Challenge & Approach ── */}
        <SectionTitle title="Challenge & Approach" />
        <Field label="The Challenge" value={caseStudyForm.challenge} onChange={update("challenge")} multiline placeholder="Client needed a scalable e-commerce platform..." />
        <Field label="Our Approach" value={caseStudyForm.approach} onChange={update("approach")} multiline placeholder="We built a modern full-stack platform..." />

        {/* ── Results ── */}
        <SectionTitle title="Results" />
        {caseStudyForm.results.map((r, i) => (
          <ItemCard
            key={i}
            index={i}
            label="Result"
            onDelete={() => {
              if (caseStudyForm.results.length <= 1) return;
              setCaseStudyForm((p) => ({
                ...p,
                results: p.results.filter((_, idx) => idx !== i),
              }));
            }}
          >
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Field
                label="Value"
                value={r.value}
                onChange={(v) => {
                  const results = [...caseStudyForm.results];
                  results[i] = { ...results[i], value: v };
                  setCaseStudyForm((p) => ({ ...p, results }));
                }}
                placeholder="10k"
              />
              <Field
                label="Detail (suffix)"
                value={r.detail}
                onChange={(v) => {
                  const results = [...caseStudyForm.results];
                  results[i] = { ...results[i], detail: v };
                  setCaseStudyForm((p) => ({ ...p, results }));
                }}
                placeholder="+"
              />
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Field
                label="Label"
                value={r.label}
                onChange={(v) => {
                  const results = [...caseStudyForm.results];
                  results[i] = { ...results[i], label: v };
                  setCaseStudyForm((p) => ({ ...p, results }));
                }}
                placeholder="Monthly Product Views"
              />
              <Field
                label="Timeframe"
                value={r.timeframe}
                onChange={(v) => {
                  const results = [...caseStudyForm.results];
                  results[i] = { ...results[i], timeframe: v };
                  setCaseStudyForm((p) => ({ ...p, results }));
                }}
                placeholder="Across furniture collections"
              />
            </Box>
          </ItemCard>
        ))}
        <AddItemBtn
          label="Add Result"
          onClick={() =>
            setCaseStudyForm((p) => ({
              ...p,
              results: [...p.results, { value: "", detail: "", label: "", timeframe: "" }],
            }))
          }
        />

        {/* ── Tech Stack ── */}
        <SectionTitle title="Tech Stack" />
        <Field label="Tech Stack (comma separated)" value={caseStudyForm.techStack} onChange={update("techStack")} placeholder="MongoDB, Express.js, React.js, Node.js" />

        {/* ── Testimonial ── */}
        <SectionTitle title="Testimonial" />
        <Field label="Quote" value={caseStudyForm.testimonial} onChange={update("testimonial")} multiline placeholder="The platform successfully transformed our online presence..." />
        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
          <Field label="Client Name" value={caseStudyForm.testimonialClient} onChange={update("testimonialClient")} placeholder="Comforty.pk Team" />
          <Field label="Role" value={caseStudyForm.testimonialRole} onChange={update("testimonialRole")} placeholder="Furniture & Design Team" />
          <Field label="Initials" value={caseStudyForm.testimonialInitials} onChange={update("testimonialInitials")} placeholder="CP" />
        </Box>
      </DialogContent>

      <ModalActions
        onCancel={onClose}
        onConfirm={onConfirm}
        confirmLabel={savingCaseStudy ? "Saving..." : "Save & Add to Page ✓"}
        confirmColor="#2e7d32"
        confirmHoverColor="#1b5e20"
        disabled={savingCaseStudy || !caseStudyForm.title}
        loading={savingCaseStudy}
      />
    </Dialog>
  );
};

export default CaseStudyModal;